// SITE3: de hele bouwtekening (de bouw-PDF) in een viewer op de pagina, zonder download (Stijn 11-10:
// "Bekijk bouwtekening zou de pdfviewer moeten laten zien. Niet 1 plankje."). Gebundeld naar
// /js/site/bouwtekening.js. Geladen op /hoe-het-werkt en /bouwplannen/<slug> als de template een bouw-PDF
// heeft (site2/bouwplannen/tekening.ts); de opmaak staat in public/css/site2/bouwtekening.css.
//
// Afspraak met de HTML: een knop of link met data-tekening="<slug>". Een <button hidden> wordt hier
// zichtbaar; een <a> blijft zonder JavaScript een gewone link naar het bouwplan. Een klik opent een
// <dialog> met één pagina tegelijk op een canvas: vorige en volgende, kleiner en groter, sluiten met de
// knop of Escape. De focus blijft in de dialog (showModal) en gaat daarna terug naar de knop.
//
// pdf.js (pdfjs-dist, Apache-2.0, legacy-build voor oudere Safari) laadt pas bij de eerste klik, als
// los stuk uit /js/_gedeeld/ (_pdfjs.ts, esbuild splitting). De worker komt van /js/site/pdf-worker.js op
// de eigen server. Geen tekstlaag, geen links in de PDF, geen download- of printknop.

import type { PDFDocumentProxy, RenderTask } from "./_pdfjs.ts";

type PdfJs = typeof import("./_pdfjs.ts");

/** Zoomstappen, als factor op "past in de breedte". */
const ZOOM = [1, 1.5, 2, 3] as const;
/** Safari op de iPhone tekent een canvas boven ongeveer 16,7 miljoen pixels niet meer. */
const MAX_PIXELS = 16_000_000;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Zelfde adres als tekeningPad() in site2/bouwplannen/tekening.ts. */
function pdfAdres(slug: string): string {
  return `/bouwplannen/${slug}/tekening.pdf`;
}

let pdfjs: Promise<PdfJs> | null = null;

function laadPdfJs(): Promise<PdfJs> {
  pdfjs ??= import("./_pdfjs.ts").then(
    (m) => {
      // De worker staat naast dit script, met dezelfde versie (?v=) in het adres.
      m.GlobalWorkerOptions.workerSrc = new URL(`pdf-worker.js${new URL(import.meta.url).search}`, import.meta.url).href;
      return m;
    },
    (fout: unknown) => {
      pdfjs = null; // een volgende klik probeert het opnieuw
      throw fout;
    },
  );
  return pdfjs;
}

function maak<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, tekst = ""): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  el.className = klasse;
  if (tekst) el.textContent = tekst;
  return el;
}

function knop(tekst: string, klasse: string, label?: string): HTMLButtonElement {
  const k = maak("button", klasse, tekst);
  k.type = "button";
  if (label) k.setAttribute("aria-label", label);
  return k;
}

class Viewer {
  private readonly dlg: HTMLDialogElement;
  private readonly vlak: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly melding: HTMLElement;
  private readonly teller: HTMLElement;
  private readonly sluit: HTMLButtonElement;
  private readonly vorige: HTMLButtonElement;
  private readonly volgende: HTMLButtonElement;
  private readonly kleiner: HTMLButtonElement;
  private readonly groter: HTMLButtonElement;
  private doc: PDFDocumentProxy | null = null;
  private adres = "";
  private nr = 1;
  private zoom = 0;
  private taak: RenderTask | null = null;
  private terug: HTMLElement | null = null;
  /** Volgnummers: een oudere laad- of tekenactie doet niets meer als er intussen een nieuwe is. */
  private laadBeurt = 0;
  private tekenBeurt = 0;
  private resizeTimer = 0;

  constructor() {
    this.dlg = maak("dialog", "tekening-dialoog");
    this.dlg.setAttribute("aria-labelledby", "tekening-titel");

    const kop = maak("div", "tekening-kop");
    const titel = maak("h2", "tekening-titel", "Bouwtekening");
    titel.id = "tekening-titel";
    this.kleiner = knop("−", "btn btn-secondary btn-sm tekening-zoom", "Kleiner");
    this.groter = knop("+", "btn btn-secondary btn-sm tekening-zoom", "Groter");
    this.sluit = knop("Sluiten", "btn btn-secondary btn-sm");
    kop.append(titel, this.kleiner, this.groter, this.sluit);

    this.vlak = maak("div", "tekening-vlak");
    this.vlak.tabIndex = 0;
    this.vlak.setAttribute("role", "region");
    this.vlak.setAttribute("aria-label", "Bouwtekening");
    this.melding = maak("p", "tekening-melding");
    this.melding.setAttribute("role", "status");
    this.canvas = maak("canvas", "tekening-blad");
    this.canvas.setAttribute("role", "img");
    this.canvas.hidden = true;
    // Geen "Afbeelding opslaan" via de rechtermuisknop.
    this.canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    this.vlak.append(this.melding, this.canvas);

    const balk = maak("div", "tekening-balk");
    this.vorige = knop("Vorige", "btn btn-secondary btn-sm", "Vorige pagina");
    this.teller = maak("span", "tekening-teller");
    this.teller.setAttribute("aria-live", "polite");
    this.volgende = knop("Volgende", "btn btn-secondary btn-sm", "Volgende pagina");
    balk.append(this.vorige, this.teller, this.volgende);

    this.dlg.append(kop, this.vlak, balk);
    document.body.append(this.dlg);

    this.sluit.addEventListener("click", () => this.dlg.close());
    this.vorige.addEventListener("click", () => this.naarPagina(this.nr - 1));
    this.volgende.addEventListener("click", () => this.naarPagina(this.nr + 1));
    this.kleiner.addEventListener("click", () => this.zetZoom(this.zoom - 1));
    this.groter.addEventListener("click", () => this.zetZoom(this.zoom + 1));
    // Een klik op de achtergrond naast de dialog sluit hem ook.
    this.dlg.addEventListener("click", (e) => {
      if (e.target === this.dlg) this.dlg.close();
    });
    this.dlg.addEventListener("keydown", (e) => this.toets(e));
    this.dlg.addEventListener("close", () => this.opgeruimd());
    window.addEventListener("resize", () => {
      if (!this.dlg.open) return;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => void this.teken(), 150);
    });
    this.zetKnoppen();
  }

  async open(adres: string, terug: HTMLElement): Promise<void> {
    this.terug = terug;
    if (!this.dlg.open) this.dlg.showModal();
    document.documentElement.classList.add("tekening-open");
    this.sluit.focus();
    if (this.doc && this.adres === adres) {
      await this.teken();
      return;
    }
    const beurt = ++this.laadBeurt;
    this.adres = adres;
    this.nr = 1;
    this.zoom = 0;
    this.canvas.hidden = true;
    this.toonMelding("De bouwtekening laadt.");
    this.zetKnoppen();
    try {
      const m = await laadPdfJs();
      const oud = this.doc;
      this.doc = null;
      if (oud) void oud.loadingTask.destroy();
      const taak = m.getDocument({ url: adres, useWasm: false, enableXfa: false, verbosity: m.VerbosityLevel.ERRORS });
      const doc = await taak.promise;
      if (beurt !== this.laadBeurt) {
        void taak.destroy();
        return;
      }
      this.doc = doc;
      await this.teken();
    } catch {
      if (beurt !== this.laadBeurt) return;
      this.adres = "";
      this.canvas.hidden = true;
      this.toonMelding("De bouwtekening kon niet laden. Probeer het later opnieuw.");
      this.zetKnoppen();
    }
  }

  private toonMelding(tekst: string) {
    this.melding.textContent = tekst;
    this.melding.hidden = !tekst;
  }

  private naarPagina(nr: number) {
    if (!this.doc || nr < 1 || nr > this.doc.numPages || nr === this.nr) return;
    this.nr = nr;
    this.vlak.scrollTo(0, 0);
    void this.teken();
  }

  private zetZoom(stap: number) {
    if (stap < 0 || stap >= ZOOM.length || stap === this.zoom) return;
    // Houd het midden van wat je ziet in beeld.
    const v = this.vlak;
    const midX = (v.scrollLeft + v.clientWidth / 2) / Math.max(v.scrollWidth, 1);
    const midY = (v.scrollTop + v.clientHeight / 2) / Math.max(v.scrollHeight, 1);
    this.zoom = stap;
    void this.teken().then(() => {
      v.scrollTo(midX * v.scrollWidth - v.clientWidth / 2, midY * v.scrollHeight - v.clientHeight / 2);
    });
  }

  private toets(e: KeyboardEvent) {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const pijl = this.zoom === 0; // ingezoomd scrollen de pijltjes door de pagina
    if (e.key === "PageDown" || (pijl && e.key === "ArrowRight")) this.naarPagina(this.nr + 1);
    else if (e.key === "PageUp" || (pijl && e.key === "ArrowLeft")) this.naarPagina(this.nr - 1);
    else if (e.key === "+" || e.key === "=") this.zetZoom(this.zoom + 1);
    else if (e.key === "-") this.zetZoom(this.zoom - 1);
    else return;
    e.preventDefault();
  }

  private zetKnoppen() {
    const n = this.doc?.numPages ?? 0;
    this.vorige.disabled = !n || this.nr <= 1;
    this.volgende.disabled = !n || this.nr >= n;
    this.kleiner.disabled = !n || this.zoom <= 0;
    this.groter.disabled = !n || this.zoom >= ZOOM.length - 1;
    this.teller.textContent = n ? `Pagina ${this.nr} van ${n}` : "";
  }

  /** Tekent de huidige pagina, passend in de breedte maal de zoomstap, scherp op het scherm. */
  private async teken(): Promise<void> {
    const doc = this.doc;
    if (!doc || !this.dlg.open) return;
    const beurt = ++this.tekenBeurt;
    const vorige = this.taak;
    this.taak = null;
    if (vorige) {
      vorige.cancel();
      await vorige.promise.catch(() => {});
    }
    try {
      const pagina = await doc.getPage(this.nr);
      if (beurt !== this.tekenBeurt) return;
      const basis = pagina.getViewport({ scale: 1 });
      const stijl = getComputedStyle(this.vlak);
      const ruimte = this.vlak.clientWidth - parseFloat(stijl.paddingLeft) - parseFloat(stijl.paddingRight);
      const viewport = pagina.getViewport({ scale: (Math.max(ruimte, 160) / basis.width) * ZOOM[this.zoom]! });
      const oppervlak = viewport.width * viewport.height;
      const dpr = Math.min(window.devicePixelRatio || 1, Math.sqrt(MAX_PIXELS / oppervlak));
      const c = this.canvas;
      c.width = Math.floor(viewport.width * dpr);
      c.height = Math.floor(viewport.height * dpr);
      c.style.width = `${Math.floor(viewport.width)}px`;
      c.style.height = `${Math.floor(viewport.height)}px`;
      c.setAttribute("aria-label", `Pagina ${this.nr} van ${doc.numPages} van de bouwtekening`);
      const taak = pagina.render({ canvas: c, viewport, transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0] });
      this.taak = taak;
      this.zetKnoppen();
      await taak.promise;
      if (beurt !== this.tekenBeurt) return;
      this.taak = null;
      this.canvas.hidden = false;
      this.toonMelding("");
    } catch (fout) {
      if ((fout as { name?: string } | null)?.name === "RenderingCancelledException" || beurt !== this.tekenBeurt) return;
      this.canvas.hidden = true;
      this.toonMelding("Deze pagina kon niet laden. Probeer het later opnieuw.");
    }
  }

  /** Na het sluiten: tekenen stoppen, het geheugen van het canvas vrijgeven en de focus terug. */
  private opgeruimd() {
    this.tekenBeurt += 1;
    this.taak?.cancel();
    this.taak = null;
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.canvas.hidden = true;
    clearTimeout(this.resizeTimer);
    document.documentElement.classList.remove("tekening-open");
    if (this.terug?.isConnected) this.terug.focus();
  }
}

let viewer: Viewer | null = null;

function koppel(el: HTMLElement) {
  const slug = el.dataset.tekening ?? "";
  if (!SLUG.test(slug)) return;
  el.addEventListener("click", (e) => {
    // Bij een link: nieuw tabblad en dergelijke laten we aan de browser.
    if (el instanceof HTMLAnchorElement && (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    e.preventDefault();
    viewer ??= new Viewer();
    void viewer.open(pdfAdres(slug), el);
  });
  if (el instanceof HTMLButtonElement) el.hidden = false;
}

// Zonder <dialog> (heel oude browsers) blijft de link een link en de verborgen knop verborgen.
if (typeof HTMLDialogElement === "function" && "showModal" in HTMLDialogElement.prototype) {
  for (const el of document.querySelectorAll<HTMLElement>("[data-tekening]")) koppel(el);
}

// Eigen scope (module), zodat namen niet botsen met andere browserbestanden in de typecheck.
export {};
